import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { API_URL, api } from '../../services/api';

interface ArticleTissu {
  id: number;
  is_client_cloth: number;
  description: string;
  tissu_nom: string;
  tissu_image: string;
  tissu_unite: string;
  quantite: number;
}

interface Article {
  id: number;
  type_habit_nom: string;
  modele_nom: string;
  modele_image: string;
  quantite: number;
  tissus: ArticleTissu[];
}

interface Affectation {
  id: number;
  type: string;
  personnel_nom: string;
  statut: string;
  tache: string;
  quantite: number;
}

interface OrderDetails {
  id: number;
  code: string;
  client_nom: string;
  client_telephone: string;
  date_commande: string;
  date_livraison: string;
  notes: string;
  articles: Article[];
  mesures: Record<string, string>;
  affectations: Affectation[];
}

export default function FicheAtelierPublic() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoomImage, setZoomImage] = useState<string | null>(null);

  useEffect(() => {
    if (!id || !token) {
      setError("Paramètres d'accès incorrects ou jeton de sécurité manquant.");
      setLoading(false);
      return;
    }

    const fetchDetails = async () => {
      try {
        const res = await fetch(`${API_URL}/commandes/atelier-public/${id}?token=${token}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || `Erreur serveur (${res.status})`);
        }
        const data = await res.json();
        setOrder(data);
      } catch (err: any) {
        setError(err.message || "Impossible de charger la fiche d'atelier.");
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [id, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foret-500"></div>
        <p className="mt-4 text-sm font-semibold text-gray-500">Chargement de la fiche d'atelier...</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-2xl mb-4">
          <i className="ri-error-warning-line" />
        </div>
        <h2 className="font-playfair font-bold text-lg text-gray-900">Accès Refusé / Commande Introuvable</h2>
        <p className="mt-2 text-sm text-gray-500 max-w-sm">{error || "Cette commande n'existe pas ou le lien a expiré."}</p>
      </div>
    );
  }

  const hasMesures = Object.keys(order.mesures || {}).length > 0;

  return (
    <div className="min-h-screen bg-gray-50 font-nunito pb-12">
      {/* Sticky Top Header */}
      <header className="bg-white border-b border-gray-100 px-6 py-4 sticky top-0 z-10 shadow-sm flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-foret-500 flex items-center justify-center text-white text-lg font-bold">
            B
          </div>
          <div>
            <h1 className="font-playfair font-bold text-base text-gray-900">Atelier Bassoum</h1>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Fiche Technique Atelier</p>
          </div>
        </div>
        <span className="px-3 py-1 bg-foret-50 text-foret-700 text-xs font-black rounded-full border border-foret-100">
          {order.code}
        </span>
      </header>

      <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
        {/* Delivery Alert Panel */}
        <div className="bg-gradient-to-r from-foret-900 to-foret-800 text-white rounded-3xl p-6 shadow-xl shadow-foret-900/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase font-bold text-foret-300 tracking-widest">Client</p>
            <h2 className="text-xl font-bold font-playfair mt-0.5">{order.client_nom}</h2>
            {order.client_telephone && (
              <p className="text-xs text-foret-200 mt-1 flex items-center gap-1">
                <i className="ri-phone-line" /> {order.client_telephone}
              </p>
            )}
          </div>
          <div className="sm:text-right bg-white/10 px-4 py-3 rounded-2xl border border-white/10">
            <p className="text-xs uppercase font-bold text-foret-300 tracking-widest">Livraison souhaitée</p>
            <p className="text-lg font-black mt-0.5 text-or-300">
              {new Date(order.date_livraison).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}
            </p>
          </div>
        </div>

        {/* Grid layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Left: Articles and Models */}
          <div className="md:col-span-7 space-y-6">
            <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm space-y-6">
              <div>
                <h3 className="font-playfair font-bold text-lg text-gray-900 flex items-center gap-2">
                  <i className="ri-scissors-cut-line text-foret-600" />
                  Tenues à confectionner ({order.articles.length})
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Détail des modèles et des tissus associés</p>
              </div>

              <div className="space-y-6 divide-y divide-gray-100">
                {order.articles.map((art, idx) => (
                  <div key={art.id} className={`pt-6 first:pt-0 space-y-4`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="font-bold text-base text-gray-900 uppercase tracking-wide">
                          {art.type_habit_nom}
                        </h4>
                        <p className="text-xs text-gray-400 mt-0.5">Modèle : <strong>{art.modele_nom || "Sur mesure"}</strong></p>
                      </div>
                    </div>

                    {/* Model image and fabric details */}
                    <div className="flex flex-col sm:flex-row gap-4 items-start">
                      {art.modele_image ? (
                        <div 
                          className="w-full sm:w-28 aspect-[3/4] rounded-2xl overflow-hidden border border-gray-100 shadow-sm relative group cursor-pointer"
                          onClick={() => setZoomImage(art.modele_image)}
                        >
                          <img 
                            src={api.getProxyUrl(art.modele_image)} 
                            alt={art.type_habit_nom} 
                            className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" 
                          />
                          <div className="absolute inset-0 bg-black/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="ri-zoom-in-line text-white text-xl" />
                          </div>
                        </div>
                      ) : (
                        <div className="w-full sm:w-28 aspect-[3/4] rounded-2xl bg-gray-50 border border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-400 gap-1">
                          <i className="ri-image-line text-xl" />
                          <span className="text-[10px] font-bold">Pas d'image</span>
                        </div>
                      )}

                      {/* Fabrics */}
                      <div className="flex-1 space-y-2 w-full">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Tissus alloués</p>
                        {art.tissus && art.tissus.length > 0 ? (
                          <div className="space-y-2">
                            {art.tissus.map((tis) => (
                              <div 
                                key={tis.id} 
                                className={`p-3 rounded-xl border flex items-center justify-between gap-3 ${
                                  tis.is_client_cloth ? 'bg-gray-50/50 border-gray-100' : 'bg-terre-50/20 border-terre-100'
                                }`}
                              >
                                <div>
                                  <p className="text-xs font-bold text-gray-800">
                                    {tis.is_client_cloth ? 'Tissu client' : 'Tissu boutique'}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {tis.is_client_cloth ? tis.description : tis.tissu_nom}
                                  </p>
                                </div>
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${
                                  tis.is_client_cloth ? 'bg-gray-200 text-gray-700' : 'bg-terre-500 text-white shadow-sm'
                                }`}>
                                  {tis.quantite} {tis.tissu_unite || 'm'}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs italic text-gray-400">Aucun tissu spécifiquement associé.</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Atelier tasks */}
            <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm space-y-4">
              <div>
                <h3 className="font-playfair font-bold text-lg text-gray-900 flex items-center gap-2">
                  <i className="ri-calendar-todo-line text-foret-600" />
                  Avancement Atelier
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Suivi de la coupe, couture et broderie</p>
              </div>

              {order.affectations && order.affectations.length > 0 ? (
                <div className="space-y-3">
                  {order.affectations.map((aff) => (
                    <div key={aff.id} className="p-3 rounded-2xl border border-gray-100 flex items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-md ${
                            aff.type === 'COUPE' ? 'bg-blue-50 text-blue-600 border border-blue-100' :
                            aff.type === 'COUTURE' ? 'bg-foret-50 text-foret-600 border border-foret-100' :
                            'bg-or-50 text-or-600 border border-or-100'
                          }`}>
                            {aff.type}
                          </span>
                          <span className="text-xs font-bold text-gray-800">{aff.tache}</span>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1">Assigné à : <strong>{aff.personnel_nom}</strong></p>
                      </div>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                        aff.statut === 'TERMINE' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {aff.statut === 'TERMINE' ? 'Terminé' : 'En cours'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs italic text-gray-400 text-center py-4">Aucune assignation enregistrée pour le moment.</p>
              )}
            </div>
          </div>

          {/* Right: Measurements & Notes */}
          <div className="md:col-span-5 space-y-6">
            {/* Notes */}
            {order.notes && (
              <div className="bg-or-50 border border-or-200/50 rounded-3xl p-6 shadow-sm space-y-2">
                <h3 className="font-playfair font-bold text-gray-900 flex items-center gap-1.5 text-sm uppercase tracking-wide">
                  <i className="ri-feedback-line text-or-600" />
                  Consignes du Gérant
                </h3>
                <p className="text-xs text-gray-700 leading-relaxed font-semibold italic">{order.notes}</p>
              </div>
            )}

            {/* Measures */}
            <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm space-y-4">
              <div>
                <h3 className="font-playfair font-bold text-lg text-gray-900 flex items-center gap-2">
                  <i className="ri-pencil-ruler-line text-foret-600" />
                  Mesures du Client
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Valeurs de coupe enregistrées</p>
              </div>

              {hasMesures ? (
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(order.mesures).map(([nomChamp, valeur]) => (
                    <div key={nomChamp} className="bg-gray-50 rounded-xl p-3 border border-gray-100 flex flex-col justify-between">
                      <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{nomChamp}</span>
                      <span className="text-base font-black text-gray-800 mt-1">{valeur}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-100">
                  <p className="text-xs text-gray-400">Aucune mesure enregistrée pour cette commande.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image zoom modal */}
      {zoomImage && (
        <div 
          className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setZoomImage(null)}
        >
          <div className="max-w-2xl max-h-[85vh] overflow-hidden rounded-2xl shadow-2xl relative">
            <img src={api.getProxyUrl(zoomImage)} className="w-full h-auto object-contain" alt="zoom" />
            <button 
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60"
              onClick={() => setZoomImage(null)}
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
